import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RotateCcw, User, CalendarClock, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { AuthContext } from "@/App";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NextActionModal
 *
 * Shown when a user clicks "Specify next action" (or the existing chip) on a
 * blocked row. Captures a mandatory plan + target date. The current user's
 * name is persisted alongside the entry so ownership is unambiguous.
 *
 * Props:
 *   open       boolean
 *   item       line-item object
 *   onClose    () => void
 *   onSaved    (updatedItem) => void — parent uses this to patch global state
 */
export default function NextActionModal({ open, item, onClose, onSaved }) {
  const { user } = useContext(AuthContext) || {};
  const [action, setAction] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      setAction(item.next_action || "");
      setDate(item.next_action_date || "");
    }
    if (!open) { setAction(""); setDate(""); setSaving(false); }
  }, [open, item]);

  if (!item) return null;

  const trimmed = action.trim();
  const dateValid = date && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const valid = trimmed.length >= 5 && dateValid && !saving;

  const ownerName = user?.name || user?.email || "You";
  const ownerRole = user?.role || "";

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/line-items/${item.id}/next-action`, {
        next_action: trimmed,
        next_action_date: date,
      });
      toast.success("Next action recorded");
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save next action");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose && onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[500px] max-w-[95vw] p-0 overflow-hidden"
        data-testid="next-action-modal"
      >
        <div className="h-[3px] w-full bg-amber-500" />
        <DialogHeader className="px-5 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <RotateCcw className="w-4 h-4 text-amber-500" />
            <span>Specify Next Action</span>
          </DialogTitle>
          <DialogDescription className="text-[11px] leading-relaxed">
            This item is blocked. Describe the single, concrete next step that will move it forward, and the date by which it should happen. Your name is attached as the owner so others know who to follow up with.
          </DialogDescription>
        </DialogHeader>

        {/* Blocked reason recap — helps the user frame their action */}
        <div className="mx-5 mb-3 mt-1 px-3 py-2 rounded-md border border-red-500/25 bg-red-500/5 text-[11px] flex items-start gap-2">
          <ShieldAlert className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <span className="font-bold uppercase text-[9px] tracking-widest text-red-600 mr-2">Blocked</span>
            <span className="text-[hsl(var(--foreground))]">{item.block_reason || "—"}</span>
          </div>
        </div>

        <div className="px-5 pb-3 space-y-4">
          {/* Action */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">
              Next Action <span className="text-rose-400 normal-case font-normal">(required · min 5 chars)</span>
            </label>
            <textarea
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. Follow up with vendor on revised delivery schedule"
              rows={3}
              maxLength={400}
              className="w-full px-3 py-2 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
              data-testid="next-action-input"
            />
            <div className="flex justify-end text-[9px] text-[hsl(var(--muted-foreground))] font-mono-data">{trimmed.length}/400</div>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> Target Date <span className="text-rose-400 normal-case font-normal">(required)</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-mono-data text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid="next-action-date-input"
            />
          </div>

          {/* Ownership recap */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]" data-testid="next-action-owner-banner">
            <User className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" />
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-tight">
              This will be attached to you — <span className="font-bold text-[hsl(var(--foreground))]">{ownerName}</span>{ownerRole && <span className="ml-1 opacity-80">({ownerRole})</span>} — as the accountable owner.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 flex items-center justify-end gap-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <button onClick={onClose} disabled={saving} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="next-action-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={!valid}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="next-action-confirm-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save &amp; Take Ownership
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
